/**
 * Cartão de leitura do conforto do setor do posto (D3): as medições são lidas, nunca digitadas aqui.
 * Mostra a medição mais recente de cada parâmetro, o limite do sistema e a situação com palavra.
 * Falha na leitura não derruba a etapa: o cartão diz que não leu e o resto segue utilizável.
 */
import React from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import { pode } from '../../../../authorization/pode';
import { useVeredito } from '../../../../authorization/useVeredito';
import { DataTable } from '../../../../components';
import { MainText } from '../../MainText';
import RouteActionButton from '../../../../components/common/RouteActionButton';
import type { ErgonomicComfortParameter } from '../../../../types/ergonomicWorkstation';
import { useComfort } from './useComfort';

const MEASUREMENTS_ROUTE = '/environmental-measurements';

/** Link para as medições só para quem pode lê-las; os demais recebem o encaminhamento em texto. */
export const MeasurementsLink: React.FC = () => {
  const veredito = useVeredito(MEASUREMENTS_ROUTE, pode('environmental_measurement.read'));
  if (!veredito.liberado) {
    return (
      <Typography component="span" variant="body1">
        Para ver as medições, peça acesso a quem administra o seu perfil.
      </Typography>
    );
  }
  return (
    <RouteActionButton
      para={MEASUREMENTS_ROUTE}
      exige={pode('environmental_measurement.read')}
      variant="text"
      size="small"
      sx={{ minHeight: 44 }}
    >
      Abrir medições
    </RouteActionButton>
  );
};

const SITUATION_LABELS: Record<string, { label: string; color: 'success' | 'error' | 'default' }> = {
  dentro: { label: 'Dentro do limite', color: 'success' },
  fora: { label: 'Fora do limite', color: 'error' },
  nao_avaliado: { label: 'Não medido', color: 'default' },
};

const situationOf = (parameter: ErgonomicComfortParameter) =>
  SITUATION_LABELS[parameter.situation ?? 'nao_avaliado'] ?? SITUATION_LABELS.nao_avaliado;

const formatMeasured = (parameter: ErgonomicComfortParameter): string => {
  if (parameter.measuredValue === null) return '—';
  const value = String(Number(parameter.measuredValue)).replace('.', ',');
  return parameter.unit ? `${value} ${parameter.unit}` : value;
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '';
  const [year, month, day] = iso.slice(0, 10).split('-');
  return `${day}/${month}/${year}`;
};

interface ComfortCardProps {
  workstationId: number;
  departmentName: string | null;
}

export const ComfortCard: React.FC<ComfortCardProps> = ({ workstationId, departmentName }) => {
  const { comfort, loading, error, reload } = useComfort(workstationId);
  const sector = departmentName ? `do setor ${departmentName}` : 'do setor do posto';
  const hasOutOfLimit = comfort?.parameters.some((parameter) => parameter.situation === 'fora') ?? false;

  return (
    <Box component="section" sx={{ mb: 2 }} aria-labelledby="comfort-title">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography id="comfort-title" variant="h6" component="h3">
          Conforto do ambiente
        </Typography>
        <Chip size="small" variant="outlined" label={`Lido das medições ${sector}`} sx={{ fontSize: '1rem' }} />
      </Box>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Não se digita aqui. O documento usa a medição mais recente de cada parâmetro dentro do período dele.
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
          <CircularProgress size={24} aria-label="Lendo as medições" />
          <Typography variant="body1">Lendo as medições de conforto...</Typography>
        </Box>
      )}

      {!loading && error && (
        <Alert
          severity="warning"
          sx={{ fontSize: '1rem' }}
          action={
            <Button color="inherit" onClick={reload} sx={{ minHeight: 44 }}>
              Tentar de novo
            </Button>
          }
        >
          {error} O resto desta etapa continua valendo; o conforto você confere depois.
        </Alert>
      )}

      {!loading && !error && comfort && !comfort.hasAnyMeasurement && (
        <Alert severity="info" sx={{ fontSize: '1rem' }} action={<MeasurementsLink />}>
          Este setor ainda não tem medição de conforto. Sem ela, o conforto sai como "—" no documento. Meça no setor ou
          em um cargo do setor.
        </Alert>
      )}

      {!loading && !error && comfort && comfort.hasAnyMeasurement && (
        <>
          <DataTable<ErgonomicComfortParameter>
            embedded
            rows={comfort.parameters}
            getRowId={(row) => row.parameter}
            emptyState={{
              title: 'Nenhum parâmetro de conforto neste setor',
              description: 'As medições de conforto do setor aparecem aqui assim que forem cadastradas.',
            }}
            columns={[
              { id: 'parameter', label: 'Parâmetro', primary: true, render: (row) => <MainText>{row.label}</MainText> },
              {
                id: 'measured',
                label: 'Medido',
                render: (row) => (
                  <MainText>
                    {formatMeasured(row)}
                    {row.measuredAt && (
                      <Typography component="span" variant="body1" sx={{ display: 'block', fontSize: '1rem' }}>
                        em {formatDate(row.measuredAt)}
                      </Typography>
                    )}
                  </MainText>
                ),
              },
              { id: 'limit', label: 'Limite do sistema', render: (row) => <MainText>{row.limit ?? '—'}</MainText> },
              {
                id: 'situation',
                label: 'Situação',
                render: (row) => {
                  const situation = situationOf(row);
                  return (
                    <Chip
                      size="medium"
                      variant="outlined"
                      color={situation.color}
                      label={situation.label}
                      sx={{ fontSize: '1rem' }}
                    />
                  );
                },
              },
            ]}
          />
          {hasOutOfLimit && (
            <Alert severity="warning" sx={{ fontSize: '1rem', mt: 2 }} action={<MeasurementsLink />}>
              O limite usado aqui é o do sistema. O modelo do cliente pode citar outro limite para o mesmo parâmetro, e
              por ele a mesma medição poderia estar adequada. Confira antes de emitir.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};
